import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, ShieldOff, Copy } from 'lucide-react';
import { mfaStatus, mfaEnroll, mfaActivate, mfaDisable, readApiError } from '@/lib/auth-api';

/**
 * TOTP two-factor settings: enroll (show otpauth secret) → activate with a
 * code → store recovery codes; or disable with a current code. Pairs with the
 * MFA-aware login flow on LoginPage.
 */
export function MfaSettings() {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [enroll, setEnroll] = useState<{ otpauthUrl: string; secretBase32: string } | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { mfaStatus().then((s) => setEnabled(s.enabled)).catch(() => setEnabled(false)); }, []);

  const start = async () => {
    setBusy(true); setError(null);
    try { setEnroll(await mfaEnroll()); } catch (e) { setError(readApiError(e, 'Could not start enrolment')); } finally { setBusy(false); }
  };
  const activate = async () => {
    setBusy(true); setError(null);
    try {
      const { recoveryCodes } = await mfaActivate(code.trim());
      setRecovery(recoveryCodes); setEnabled(true); setEnroll(null); setCode('');
    } catch (e) { setError(readApiError(e, 'Invalid code')); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true); setError(null);
    try { await mfaDisable(code.trim()); setEnabled(false); setCode(''); } catch (e) { setError(readApiError(e, 'Invalid code')); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-center gap-2">
        {enabled ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldOff className="h-5 w-5 text-muted-foreground" />}
        <h2 className="text-base font-semibold text-foreground">
          {t('mfa.title', { defaultValue: 'Two-factor authentication' })}
        </h2>
        <span className={`ms-auto text-[11px] uppercase tracking-wider font-semibold ${enabled ? 'text-emerald-600' : 'text-muted-foreground'}`}>
          {enabled ? t('mfa.on', { defaultValue: 'On' }) : t('mfa.off', { defaultValue: 'Off' })}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('mfa.desc', { defaultValue: 'Protect your account with a time-based code from an authenticator app (Google Authenticator, 1Password, Authy).' })}
      </p>

      {error && <div role="alert" className="rounded-lg border border-warn-200 bg-warn-50 text-warn-700 px-3 py-2 text-[13px]">{error}</div>}

      {recovery && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-[13px] font-semibold text-emerald-800">{t('mfa.recoveryTitle', { defaultValue: 'Save your recovery codes' })}</p>
          <p className="text-[12px] text-emerald-700 mb-2">{t('mfa.recoveryHint', { defaultValue: 'Each can be used once if you lose your device. They will not be shown again.' })}</p>
          <div className="grid grid-cols-2 gap-1 font-mono text-[12px] text-emerald-900">
            {recovery.map((c) => <div key={c}>{c}</div>)}
          </div>
          <button onClick={() => navigator.clipboard?.writeText(recovery.join('\n'))} className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700 hover:underline">
            <Copy className="h-3 w-3" />{t('mfa.copy', { defaultValue: 'Copy all' })}
          </button>
        </div>
      )}

      {enabled === false && !enroll && (
        <button onClick={start} disabled={busy} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
          {t('mfa.enable', { defaultValue: 'Enable two-factor' })}
        </button>
      )}

      {enroll && (
        <div className="space-y-3 border-t border-border pt-4">
          <p className="text-[13px] text-foreground">{t('mfa.scan', { defaultValue: 'Add this secret to your authenticator app, then enter the 6-digit code to confirm:' })}</p>
          <code className="block break-all rounded-lg bg-muted px-3 py-2 text-[12px] font-mono text-foreground">{enroll.secretBase32}</code>
          <div className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456"
              className="h-10 w-36 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            <button onClick={activate} disabled={busy || !code.trim()} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
              {t('mfa.confirm', { defaultValue: 'Confirm' })}
            </button>
          </div>
        </div>
      )}

      {enabled === true && !recovery && (
        <div className="flex gap-2 border-t border-border pt-4">
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="text" placeholder={t('mfa.codeToDisable', { defaultValue: 'Code to disable' })}
            className="h-10 w-44 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          <button onClick={disable} disabled={busy || !code.trim()} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted/40 disabled:opacity-50">
            {t('mfa.disable', { defaultValue: 'Disable' })}
          </button>
        </div>
      )}
    </section>
  );
}
